import React, { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  UserCheck, Plus, Mail, Phone, UserX, UserPlus, Settings,
  Search, Filter, Clock, CheckCircle, XCircle, Send, Pencil
} from 'lucide-react';
import Notification from '../components/common/Notification';
import UserPermissionsPicker from '../components/UserPermissionsPicker';
import { hasPermission } from '../utils/permissions';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionsPicker, setShowPermissionsPicker] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showConfirmDeactivate, setShowConfirmDeactivate] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, pending
  const [roleFilter, setRoleFilter] = useState('all');
  const [showDeactivated, setShowDeactivated] = useState(false); // Toggle to show deactivated users
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    hourly_rate: '',
    role_ids: []
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    hourly_rate: ''
  });

  const { user: currentUser } = useAuth();

  const showNotification = useCallback((title, message, variant = 'info') => {
    setNotification({
      isOpen: true,
      title,
      message,
      variant
    });
  }, []);

  const fetchUsers = useCallback(async (includeDeactivated = false) => {
    try {
      const response = await userService.getUsers(includeDeactivated);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification('Error', 'Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const filterUsers = useCallback(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.first_name.toLowerCase().includes(term) ||
        user.last_name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
      );
    }

    // Status filter (uses firm_active for firm-level status)
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered = filtered.filter(u => u.firm_active && !u.has_pending_invitation);
      } else if (statusFilter === 'pending') {
        filtered = filtered.filter(u => u.has_pending_invitation);
      }
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.roles && u.roles.includes(roleFilter));
    }

    // Sort: active first, then pending, then deactivated — alphabetical within each group
    filtered.sort((a, b) => {
      const aActive = a.firm_active && !a.has_pending_invitation;
      const bActive = b.firm_active && !b.has_pending_invitation;
      const aPending = a.has_pending_invitation;
      const bPending = b.has_pending_invitation;
      // Active first, then pending, then deactivated
      if (aActive !== bActive) return aActive ? -1 : 1;
      if (aPending !== bPending) return aPending ? -1 : 1;
      // Alphabetical within group
      const nameA = `${a.last_name} ${a.first_name}`.toLowerCase();
      const nameB = `${b.last_name} ${b.first_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setFilteredUsers(filtered);
  }, [users, searchTerm, statusFilter, roleFilter]);

  useEffect(() => {
    fetchUsers(showDeactivated);
  }, [fetchUsers, showDeactivated]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const fetchAvailableRoles = async () => {
    try {
      const response = await userService.getRoles();
      setAvailableRoles(response.data);
      return true;
    } catch (error) {
      console.error('Error fetching roles:', error);
      if (error.response?.status === 403) {
        showNotification('Permission Denied', 'You don\'t have permission to view available roles. Contact your firm administrator.', 'error');
      } else {
        showNotification('Error', 'Failed to fetch roles', 'error');
      }
      return false;
    }
  };

  const handleManageAccess = (user) => {
    setSelectedUser(user);
    setShowPermissionsPicker(true);
  };

  const handleDeactivateClick = (user) => {
    setUserToDeactivate(user);
    setShowConfirmDeactivate(true);
  };

  const handleDeactivateConfirm = async () => {
    if (!userToDeactivate) return;

    try {
      await userService.deactivateFromFirm(userToDeactivate.id);
      await fetchUsers(showDeactivated);
      showNotification('Success', `${userToDeactivate.first_name} ${userToDeactivate.last_name} has been deactivated from the firm`, 'success');
    } catch (error) {
      console.error('Error deactivating user from firm:', error);
      if (error.response?.status === 403) {
        showNotification('Permission Denied', 'You don\'t have permission to deactivate users. Contact your firm administrator.', 'error');
      } else {
        showNotification('Error', error.response?.data?.detail || 'Failed to deactivate user', 'error');
      }
    } finally {
      setShowConfirmDeactivate(false);
      setUserToDeactivate(null);
    }
  };

  const handleReactivateUser = async (userId, userName) => {
    try {
      await userService.reactivateInFirm(userId);
      await fetchUsers(showDeactivated);
      showNotification('Success', `${userName} has been reactivated in the firm`, 'success');
    } catch (error) {
      console.error('Error reactivating user:', error);
      if (error.response?.status === 403) {
        showNotification('Permission Denied', 'You don\'t have permission to reactivate users. Contact your firm administrator.', 'error');
      } else {
        showNotification('Error', error.response?.data?.detail || 'Failed to reactivate user', 'error');
      }
    }
  };

  const handleResendInvitation = async (userId, userName) => {
    try {
      await userService.resendInvitation(userId);
      await fetchUsers();
      showNotification('Success', `Invitation resent to ${userName}`, 'success');
    } catch (error) {
      console.error('Error resending invitation:', error);
      if (error.response?.status === 403) {
        showNotification('Permission Denied', 'You don\'t have permission to resend invitations. Contact your firm administrator.', 'error');
      } else {
        showNotification('Error', error.response?.data?.detail || 'Failed to resend invitation', 'error');
      }
    }
  };


  const handleInviteUser = async () => {
    const success = await fetchAvailableRoles();
    if (success) {
      setShowInviteModal(true);
    }
  };

  const handleSendInvitation = async () => {
    try {
      const firmId = currentUser?.active_firm_id || currentUser?.primary_firm_id;
      if (!firmId) {
        showNotification('Error', 'No firm found. Please refresh the page and try again.', 'error');
        return;
      }

      const invitationData = {
        ...inviteForm,
        firm_id: firmId,
        phone: inviteForm.phone || null,
        hourly_rate: inviteForm.hourly_rate ? parseFloat(inviteForm.hourly_rate) : null
      };

      await userService.inviteUser(invitationData);
      setShowInviteModal(false);
      setInviteForm({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        hourly_rate: '',
        role_ids: []
      });
      await fetchUsers();
      showNotification('Success', `Invitation sent to ${invitationData.email}`, 'success');
    } catch (error) {
      console.error('Error sending invitation:', error);
      if (error.response?.status === 403) {
        showNotification('Permission Denied', 'You don\'t have permission to invite users. Contact your firm administrator.', 'error');
      } else {
        showNotification('Error', error.response?.data?.detail || 'Failed to send invitation', 'error');
      }
    }
  };

  const handlePermissionsSaved = async () => {
    await fetchUsers(showDeactivated);
    setShowPermissionsPicker(false);
    setSelectedUser(null);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      hourly_rate: user.hourly_rate ? parseFloat(user.hourly_rate).toString() : ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const updateData = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        phone: editForm.phone || null,
        hourly_rate: editForm.hourly_rate ? parseFloat(editForm.hourly_rate) : null
      };
      await userService.updateUser(editingUser.id, updateData);
      setShowEditModal(false);
      setEditingUser(null);
      await fetchUsers(showDeactivated);
      showNotification('Success', `${editForm.first_name} ${editForm.last_name} updated`, 'success');
    } catch (error) {
      console.error('Error updating user:', error);
      if (error.response?.status === 403) {
        showNotification('Permission Denied', 'You don\'t have permission to edit users.', 'error');
      } else {
        showNotification('Error', error.response?.data?.detail || 'Failed to update user', 'error');
      }
    }
  };

  const getStatusBadge = (user) => {
    if (user.has_pending_invitation) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending Invitation
        </span>
      );
    } else if (!user.firm_active) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Deactivated
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    }
  };

  const getRoleColor = (role) => {
    const roleMap = {
      'Admin': 'bg-purple-100 text-purple-800',
      'Attorney': 'bg-gray-100 text-gray-800',
      'Paralegal': 'bg-green-100 text-green-800',
      'Staff': 'bg-gray-100 text-gray-800',
    };
    return roleMap[role] || 'bg-gray-100 text-gray-800';
  };

  const uniqueRoles = [...new Set(users.flatMap(u => u.roles || []))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification Toast */}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <UserCheck className="mr-3 h-8 w-8" />
            Team Members
          </h1>
          <p className="text-gray-600 mt-1">Manage your law firm team</p>
        </div>
        {hasPermission(currentUser, 'invite_user') && (
          <button
            onClick={handleInviteUser}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600 transition-colors"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Team Member
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 appearance-none"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending Invitation</option>
            </select>
          </div>

          {/* Role Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 appearance-none"
            >
              <option value="all">All Roles</option>
              {uniqueRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {/* Show Deactivated Toggle */}
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showDeactivated}
                onChange={(e) => setShowDeactivated(e.target.checked)}
                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 h-4 w-4"
              />
              <span className="ml-2 text-sm text-gray-700">Show deactivated users</span>
            </label>
          </div>
        </div>

        {/* Filter Summary */}
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredUsers.length} of {users.length} team members
          {showDeactivated && <span className="ml-1 text-gray-500">(including deactivated)</span>}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <UserCheck className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        {user.hourly_rate && (
                          <div className="text-sm text-gray-500">
                            ${parseFloat(user.hourly_rate).toFixed(2)}/hr
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      {user.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role, index) => (
                          <span key={index} className={`px-2 py-1 text-xs rounded-full ${getRoleColor(role)}`}>
                            {role}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-500">No roles</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.phone ? (
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-1" />
                        {user.phone}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {user.has_pending_invitation ? (
                        hasPermission(currentUser, 'invite_user') && (
                          <button
                            onClick={() => handleResendInvitation(user.id, `${user.first_name} ${user.last_name}`)}
                            className="text-yellow-600 hover:text-yellow-700 flex items-center"
                            title="Resend Invitation"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Resend
                          </button>
                        )
                      ) : (
                        <>
                          {hasPermission(currentUser, 'manage_users') && user.firm_active && (
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Edit User"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}

                          {hasPermission(currentUser, 'manage_user_roles') && user.firm_active && (
                            <button
                              onClick={() => handleManageAccess(user)}
                              className="text-yellow-600 hover:text-yellow-700"
                              title="Manage Access"
                            >
                              <Settings className="h-4 w-4" />
                            </button>
                          )}

                          {hasPermission(currentUser, 'manage_users') && (
                            user.firm_active ? (
                              <button
                                onClick={() => handleDeactivateClick(user)}
                                className="text-red-600 hover:text-red-900"
                                title="Deactivate from Firm"
                              >
                                <UserX className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReactivateUser(user.id, `${user.first_name} ${user.last_name}`)}
                                className="text-green-600 hover:text-green-900"
                                title="Reactivate in Firm"
                              >
                                <UserPlus className="h-4 w-4" />
                              </button>
                            )
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <UserCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' || roleFilter !== 'all'
                ? 'No team members match your filters'
                : 'No team members found'}
            </p>
          </div>
        )}
      </div>

      {/* Confirm Deactivate from Firm Modal */}
      {showConfirmDeactivate && userToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Deactivate User from Firm</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to deactivate <strong>{userToDeactivate.first_name} {userToDeactivate.last_name}</strong> from this firm?
              They will lose access to this firm's data. You can reactivate them later if needed.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleDeactivateConfirm}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
              >
                Deactivate
              </button>
              <button
                onClick={() => {
                  setShowConfirmDeactivate(false);
                  setUserToDeactivate(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Management Modal */}
      {showPermissionsPicker && selectedUser && (
        <UserPermissionsPicker
          user={selectedUser}
          onClose={() => {
            setShowPermissionsPicker(false);
            setSelectedUser(null);
          }}
          onSave={handlePermissionsSaved}
          showNotification={showNotification}
        />
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Invite New Team Member</h3>

            <div className="space-y-4 mb-6">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="user@example.com"
                />
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  required
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm({...inviteForm, first_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  required
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm({...inviteForm, last_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={inviteForm.phone}
                  onChange={(e) => setInviteForm({...inviteForm, phone: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              {/* Hourly Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate</label>
                <input
                  type="number"
                  value={inviteForm.hourly_rate}
                  onChange={(e) => setInviteForm({...inviteForm, hourly_rate: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="150.00"
                />
              </div>

              {/* Roles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles *</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {availableRoles.map((role) => (
                    <label key={role.id} className="flex items-start space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={inviteForm.role_ids.includes(role.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setInviteForm({
                              ...inviteForm,
                              role_ids: [...inviteForm.role_ids, role.id]
                            });
                          } else {
                            setInviteForm({
                              ...inviteForm,
                              role_ids: inviteForm.role_ids.filter(id => id !== role.id)
                            });
                          }
                        }}
                        className="mt-1 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                      />
                      <div>
                        <span className="text-sm font-medium">{role.name}</span>
                        {role.description && (
                          <p className="text-xs text-gray-500">{role.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSendInvitation}
                disabled={!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name || inviteForm.role_ids.length === 0}
                className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Send Invitation
              </button>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteForm({
                    email: '',
                    first_name: '',
                    last_name: '',
                    phone: '',
                    hourly_rate: '',
                    role_ids: []
                  });
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Team Member</h3>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  required
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  required
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.hourly_rate}
                    onChange={(e) => setEditForm({...editForm, hourly_rate: e.target.value})}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="150.00"
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSaveEdit}
                disabled={!editForm.first_name || !editForm.last_name}
                className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
